import { useCallback, useRef, useState } from 'react';

export type ChatDomain = 'faa-part145' | 'faa-part121' | 'easa-part145' | 'icao-annex6';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /** Present only on completed assistant messages. */
  sources?: string[];
  model?: string;
  /** True while the assistant response is still streaming. */
  streaming?: boolean;
  /** Error state (rate-limited, upstream 500, parse failure). */
  error?: string;
}

interface UseChatState {
  domain: ChatDomain;
  messages: ChatMessage[];
  isStreaming: boolean;
}

/**
 * Conversation hook for the aviation-rag chat.
 *
 * - Sends history (capped at 10 messages, as the upstream truncates older
 *   entries anyway; no benefit to shipping more).
 * - Parses the SSE stream token-by-token, appending to the in-flight
 *   assistant message so the UI can render character-by-character as it
 *   arrives.
 * - Final `{done, sources, model}` event attaches provenance to the
 *   message and flips `isStreaming` → false.
 * - `cancel()` aborts the in-flight request; the assistant message stays
 *   in the transcript with whatever content it had so far.
 */
const HISTORY_CAP = 10;

let nextId = 0;
function makeId(prefix: string): string {
  nextId += 1;
  return `${prefix}-${nextId}`;
}

export function useChat() {
  const [state, setState] = useState<UseChatState>({
    domain: 'faa-part145',
    messages: [],
    isStreaming: false,
  });
  const abortRef = useRef<AbortController | null>(null);

  const setDomain = useCallback((domain: ChatDomain) => {
    setState((s) => ({ ...s, domain }));
  }, []);

  const clear = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setState((s) => ({ ...s, messages: [], isStreaming: false }));
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setState((s) => ({
      ...s,
      isStreaming: false,
      messages: s.messages.map((m) =>
        m.streaming ? { ...m, streaming: false } : m,
      ),
    }));
  }, []);

  const send = useCallback(
    async (prompt: string) => {
      const trimmed = prompt.trim();
      if (!trimmed) return;

      const userMsg: ChatMessage = {
        id: makeId('u'),
        role: 'user',
        content: trimmed,
      };
      const assistantId = makeId('a');
      const assistantMsg: ChatMessage = {
        id: assistantId,
        role: 'assistant',
        content: '',
        streaming: true,
      };

      // Snapshot history BEFORE the new user message so the request body
      // contains the same transcript the user is replying to.
      let historyForRequest: { role: 'user' | 'assistant'; content: string }[] = [];
      let domainForRequest: ChatDomain = 'faa-part145';

      setState((s) => {
        historyForRequest = s.messages.slice(-HISTORY_CAP).map((m) => ({
          role: m.role,
          content: m.content,
        }));
        domainForRequest = s.domain;
        return {
          ...s,
          messages: [...s.messages, userMsg, assistantMsg],
          isStreaming: true,
        };
      });

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const res = await fetch('/api/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: trimmed,
            history: historyForRequest,
            domain: domainForRequest,
          }),
          signal: controller.signal,
        });

        if (!res.ok) {
          const errText = await res.text().catch(() => '');
          let errMsg = `upstream ${res.status}`;
          try {
            const parsed = JSON.parse(errText);
            if (parsed?.error) errMsg = parsed.error;
          } catch {
            // fall through with status-based message
          }
          setState((s) => ({
            ...s,
            isStreaming: false,
            messages: s.messages.map((m) =>
              m.id === assistantId
                ? { ...m, streaming: false, error: errMsg }
                : m,
            ),
          }));
          return;
        }

        if (!res.body) {
          setState((s) => ({
            ...s,
            isStreaming: false,
            messages: s.messages.map((m) =>
              m.id === assistantId
                ? { ...m, streaming: false, error: 'empty_response' }
                : m,
            ),
          }));
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        // Stream parse loop. SSE frames are `data: <json>\n\n`. We read chunks,
        // split on newlines, keep the trailing fragment buffered, and process
        // complete `data:` lines as they arrive.
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const data = line.slice(6).trim();
            if (!data) continue;

            try {
              const evt = JSON.parse(data);
              if (typeof evt.token === 'string') {
                const token = evt.token as string;
                setState((s) => ({
                  ...s,
                  messages: s.messages.map((m) =>
                    m.id === assistantId
                      ? { ...m, content: m.content + token }
                      : m,
                  ),
                }));
              } else if (evt.done === true) {
                const sources = Array.isArray(evt.sources) ? evt.sources as string[] : undefined;
                const model = typeof evt.model === 'string' ? evt.model as string : undefined;
                setState((s) => ({
                  ...s,
                  isStreaming: false,
                  messages: s.messages.map((m) =>
                    m.id === assistantId
                      ? { ...m, streaming: false, sources, model }
                      : m,
                  ),
                }));
              } else if (typeof evt.error === 'string') {
                setState((s) => ({
                  ...s,
                  isStreaming: false,
                  messages: s.messages.map((m) =>
                    m.id === assistantId
                      ? { ...m, streaming: false, error: evt.error as string }
                      : m,
                  ),
                }));
              }
            } catch {
              // Skip unparseable frames — upstream guarantees valid JSON only
              // for `data: {...}` lines; anything else is a comment/heartbeat.
            }
          }
        }
      } catch (err) {
        const aborted = err instanceof Error && err.name === 'AbortError';
        if (aborted) {
          // cancel() already updated state; no-op here.
          return;
        }
        setState((s) => ({
          ...s,
          isStreaming: false,
          messages: s.messages.map((m) =>
            m.id === assistantId
              ? { ...m, streaming: false, error: (err as Error).message }
              : m,
          ),
        }));
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
      }
    },
    [],
  );

  return {
    domain: state.domain,
    messages: state.messages,
    isStreaming: state.isStreaming,
    setDomain,
    send,
    cancel,
    clear,
  };
}
